import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { X, Eye, Download, Link as LinkIcon, Trash2, FileText, File, ArrowLeft, Upload, Calendar, MapPin, Users, FileEdit, FileCheck, Image, Save, Trash } from 'lucide-react';
import { toast } from 'sonner';
import FilePreviewModal from './FilePreviewModal';
import AIFormFill from './AIFormFill';
import FormsList from './forms/FormsList';

const ELEGANT_GOLD = '#C9A961';

const FileGalleryFullScreen = ({ 
  isOpen, 
  onClose, 
  record, 
  recordType, 
  files = [], 
  onDelete, 
  canDelete = false, 
  onUpdate, 
  users = [],
  inventoryItems = [],
  onAddItem,
  onEditItem,
  onDeleteItem
}) => {
  const [previewFile, setPreviewFile] = useState(null);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('details'); // 'details', 'forms', 'photos', 'documents'
  const fileInputRef = useRef(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  if (!isOpen) return null;

  const getUserNames = (userIds) => {
    if (!Array.isArray(users) || !userIds) return 'N/A';
    
    const userIdArray = Array.isArray(userIds) ? userIds : [userIds];
    if (userIdArray.length === 0) return 'N/A';
    
    const userNames = userIdArray.map(userId => {
      const user = users.find(u => u.id === userId);
      if (!user) return 'Unknown';
      
      // Prioritize first_name and last_name if available
      if (user.first_name && user.last_name) {
        return `${user.first_name} ${user.last_name}`;
      } else if (user.first_name) {
        return user.first_name;
      } else if (user.last_name) {
        return user.last_name;
      }
      
      // Fallback to email formatting if it looks like a name
      if (user.email && user.email.includes('@')) {
        const emailPart = user.email.split('@')[0];
        if (emailPart.includes('.')) {
          return emailPart.split('.').map(part => 
            part.charAt(0).toUpperCase() + part.slice(1)
          ).join(' ');
        }
      }
      
      // Final fallback to username with proper capitalization
      return user.username.charAt(0).toUpperCase() + user.username.slice(1);
    });
    
    return userNames.join(', ');
  };

  const handleFileUpload = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length === 0) return;

    setUploading(true);
    const token = localStorage.getItem('token');
    
    try {
      const uploadPromises = selectedFiles.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await fetch(`${backendUrl}/api/upload`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        return await response.json();
      });
      
      const uploadedFiles = await Promise.all(uploadPromises);
      const successfulUploads = uploadedFiles.filter(f => f !== null);
      
      // Update the record with new files
      const updatedFiles = [...files, ...successfulUploads];
      
      // Save to backend
      const updateUrl = `${backendUrl}/api/${recordType}s/${record.id}`;
      await fetch(updateUrl, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ...record, files: updatedFiles })
      });
      
      toast.success(`${successfulUploads.length} file(s) uploaded successfully`);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const getFileIcon = (file) => {
    const filename = file.filename?.toLowerCase() || '';
    const contentType = file.content_type?.toLowerCase() || '';
    
    if (contentType.includes('pdf') || filename.endsWith('.pdf')) {
      return <FileText className="h-24 w-24 text-red-500" />;
    } else if (contentType.includes('word') || filename.endsWith('.doc') || filename.endsWith('.docx')) {
      return <FileText className="h-24 w-24 text-[#C9A961]" />;
    } else if (contentType.includes('excel') || contentType.includes('spreadsheet') || filename.endsWith('.xls') || filename.endsWith('.xlsx')) {
      return <FileText className="h-24 w-24 text-[#C9A961]" />;
    } else if (filename.endsWith('.txt') || filename.endsWith('.md') || filename.endsWith('.note')) {
      return <FileText className="h-24 w-24" style={{ color: ELEGANT_GOLD }} />;
    } else {
      return <File className="h-24 w-24" style={{ color: ELEGANT_GOLD }} />;
    }
  };

  const handlePreview = (file, index) => {
    setPreviewFile(file);
    setPreviewIndex(index);
    setIsPreviewOpen(true);
  };

  const handleNavigate = (newIndex) => {
    if (files && files[newIndex]) {
      setPreviewFile(files[newIndex]);
      setPreviewIndex(newIndex);
    }
  };

  const handleDownload = (file) => {
    const link = document.createElement('a');
    link.href = `${process.env.REACT_APP_BACKEND_URL}/api/uploads/${file.stored_filename}`;
    link.download = file.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Download started');
  };

  const handleCopyLink = (file) => {
    navigator.clipboard.writeText(`${process.env.REACT_APP_BACKEND_URL}/api/uploads/${file.stored_filename}`);
    toast.success('File link copied!');
  };

  const getRecordTitle = () => {
    if (!record) return 'Files';
    
    // Handle different record types and their name/title fields
    const title = record.name || record.title || record.description;
    
    switch(recordType) {
      case 'project': return record.name || 'Project';
      case 'task': return record.title || record.name || 'Task';
      case 'work-order': return record.title || record.name || 'Work Order';
      case 'client': return record.name || 'Client';
      case 'invoice': return record.invoice_number ? `Invoice #${record.invoice_number}` : (record.description || 'Invoice');
      case 'expense': return record.description || record.name || 'Expense';
      case 'contract': return record.title || record.name || 'Contract';
      case 'equipment': return record.name || record.title || 'Equipment';
      case 'timesheet': return record.employee_name ? `Timesheet - ${record.employee_name}` : (record.name || record.title || 'Timesheet');
      case 'safety-report': return record.title || record.name || 'Safety Report';
      case 'certification': return record.name || record.title || 'Certification';
      case 'inventory': return record.item_name || record.name || 'Inventory Item';
      case 'report': return record.title || record.name || 'Report';
      case 'compliance': return record.title || record.name || 'Compliance';
      case 'tasks': return record.title || record.name || 'Task';  // Handle 'tasks' vs 'task'
      case 'projects': return record.name || 'Project';  // Handle 'projects' vs 'project'
      case 'inventory-project': return record.project_name || 'Project Inventory';
      default: return title || 'Files';
    }
  };

  const getRecordDetails = () => {
    switch(recordType) {
      case 'project':
        return `Status: ${record?.status || 'N/A'} | Client: ${record?.client_name || 'N/A'}`;
      case 'task':
        return `Status: ${record?.status || 'N/A'} | Priority: ${record?.priority || 'N/A'}`;
      case 'client':
        return `Email: ${record?.email || 'N/A'}`;
      case 'invoice':
        return `Amount: $${record?.amount || 0} | Status: ${record?.status || 'N/A'}`;
      case 'expense':
        return `Amount: $${record?.amount || 0} | Category: ${record?.category || 'N/A'}`;
      case 'contract':
        return `Status: ${record?.status || 'N/A'} | Client: ${record?.client_name || 'N/A'}`;
      case 'equipment':
        return `Status: ${record?.status || 'N/A'} | Location: ${record?.location || 'N/A'}`;
      case 'safety-report':
        return `Severity: ${record?.severity || 'N/A'} | Status: ${record?.status || 'N/A'}`;
      case 'certification':
        return `Employee: ${record?.employee_name || 'N/A'}`;
      case 'inventory':
        return `Quantity: ${record?.quantity || 0} ${record?.unit || ''}`;
      default:
        return '';
    }
  };

  return (
    <div className="fixed inset-0 bg-black z-50 overflow-hidden flex flex-col">
      {/* Enhanced Header */}
      <div className="border-b-2 p-6 bg-black backdrop-blur-xl shadow-2xl" style={{ borderColor: ELEGANT_GOLD }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <Button 
              variant="ghost" 
              onClick={onClose} 
              className="text-[#C9A961] hover:bg-[#C9A961]/20 hover:scale-105 transition-all border border-[#C9A961]/50"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to List
            </Button>
            <div className="flex items-center gap-4">
              {/* Save and Delete Buttons - Admin/Manager Only */}
              <Button 
                variant="ghost" 
                onClick={() => toast.info('Save to Google Drive feature coming soon!')}
                className="text-[#C9A961] hover:bg-[#C9A961]/20 hover:scale-105 transition-all border border-[#C9A961]/50"
              >
                <Save className="h-5 w-5 mr-2" />
                Save to Drive
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => {
                  if (confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
                    toast.info('Delete functionality to be implemented');
                  }
                }}
                className="text-red-500 hover:bg-red-950 hover:scale-105 transition-all border border-red-500/50"
              >
                <Trash className="h-5 w-5 mr-2" />
                Delete
              </Button>
              <img 
                src="/williams-logo-transparent.png" 
                alt="Williams Diversified LLC" 
                className="h-20 w-auto hover:scale-105 transition-transform"
              />
              <Button 
                variant="ghost" 
                onClick={onClose} 
                className="text-[#C9A961] hover:bg-[#C9A961]/20 hover:scale-105 transition-all"
              >
                <X className="h-6 w-6" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area with Enhanced Design */}
      <div className="flex-1 overflow-y-auto p-8">
        <div className="max-w-7xl mx-auto">
          {/* Record Title */}
          <h1 className="text-5xl font-bold mb-6 text-[#C9A961] flex items-center gap-4">
            <FileEdit className="h-12 w-12" />
            {getRecordTitle()}
          </h1>

          {/* Tabs Navigation */}
          <div className="mb-8 bg-black/60 backdrop-blur-xl border-2 border-[#C9A961]/50 rounded-2xl overflow-hidden">
            <div className="flex border-b border-[#C9A961]/30">
              <button
                onClick={() => setActiveTab('details')}
                className={`flex-1 px-6 py-4 font-semibold text-lg transition-all flex items-center justify-center gap-2 ${
                  activeTab === 'details'
                    ? 'bg-[#C9A961] text-black'
                    : 'text-[#C9A961] hover:bg-[#C9A961]/10'
                }`}
              >
                <FileEdit className="h-5 w-5" />
                Details
              </button>
              <button
                onClick={() => setActiveTab('forms')}
                className={`flex-1 px-6 py-4 font-semibold text-lg transition-all flex items-center justify-center gap-2 ${
                  activeTab === 'forms'
                    ? 'bg-[#C9A961] text-black'
                    : 'text-[#C9A961] hover:bg-[#C9A961]/10'
                }`}
              >
                <FileCheck className="h-5 w-5" />
                Forms
              </button>
              <button
                onClick={() => setActiveTab('photos')}
                className={`flex-1 px-6 py-4 font-semibold text-lg transition-all flex items-center justify-center gap-2 ${
                  activeTab === 'photos'
                    ? 'bg-[#C9A961] text-black'
                    : 'text-[#C9A961] hover:bg-[#C9A961]/10'
                }`}
              >
                <Image className="h-5 w-5" />
                Photos ({files.filter(f => f.content_type?.includes('image')).length})
              </button>
              <button
                onClick={() => setActiveTab('documents')}
                className={`flex-1 px-6 py-4 font-semibold text-lg transition-all flex items-center justify-center gap-2 ${
                  activeTab === 'documents'
                    ? 'bg-[#C9A961] text-black'
                    : 'text-[#C9A961] hover:bg-[#C9A961]/10'
                }`}
              >
                <FileText className="h-5 w-5" />
                Documents ({files.filter(f => !f.content_type?.includes('image')).length})
              </button>
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'details' && (
          <div>
          {/* Project Header Card */}
          <div className="mb-8 bg-black/60 backdrop-blur-xl border-2 border-[#C9A961]/50 rounded-2xl p-8 shadow-2xl hover:border-[#C9A961] transition-all">
            
            {/* Project Details in Gold Headers */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {recordType === 'project' && record && (
                <>
                  {record.address && (
                    <div className="bg-black/40 p-6 rounded-xl border border-[#C9A961]/30 hover:border-[#C9A961]/60 transition-all">
                      <div className="flex items-center gap-3 mb-3">
                        <MapPin className="h-6 w-6 text-[#C9A961]" />
                        <p className="text-xl font-bold text-[#C9A961]">Address</p>
                      </div>
                      <p className="text-white text-lg ml-9">{record.address || record.location}</p>
                    </div>
                  )}
                  
                  {record.deadline && (
                    <div className="bg-black/40 p-6 rounded-xl border border-[#C9A961]/30 hover:border-[#C9A961]/60 transition-all">
                      <div className="flex items-center gap-3 mb-3">
                        <Calendar className="h-6 w-6 text-[#C9A961]" />
                        <p className="text-xl font-bold text-[#C9A961]">Deadline</p>
                      </div>
                      <p className="text-white text-lg ml-9">
                        {new Date(record.deadline).toLocaleDateString('en-US', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </p>
                    </div>
                  )}
                  
                  <div className="bg-black/40 p-6 rounded-xl border border-[#C9A961]/30 hover:border-[#C9A961]/60 transition-all lg:col-span-2">
                    <div className="flex items-center gap-3 mb-3">
                      <FileText className="h-6 w-6 text-[#C9A961]" />
                      <p className="text-xl font-bold text-[#C9A961]">Description</p>
                    </div>
                    <p className="text-white text-lg ml-9">{record.description || 'No description provided'}</p>
                  </div>
                  
                  <div className="bg-black/40 p-6 rounded-xl border border-[#C9A961]/30 hover:border-[#C9A961]/60 transition-all lg:col-span-2">
                    <div className="flex items-center gap-3 mb-3">
                      <Users className="h-6 w-6 text-[#C9A961]" />
                      <p className="text-xl font-bold text-[#C9A961]">Assigned Team Members</p>
                    </div>
                    <p className="text-white text-lg ml-9">{getUserNames(record.assigned_to)}</p>
                  </div>
                </>
              )}

              {/* Inventory Section for Projects */}
              {recordType === 'project' && inventoryItems && inventoryItems.length > 0 && (
                <div className="space-y-4 w-full mt-6 pt-6 border-t" style={{ borderColor: ELEGANT_GOLD }}>
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold" style={{ color: ELEGANT_GOLD }}>Project Inventory</h3>
                    {onAddItem && (
                      <button 
                        onClick={onAddItem}
                        className="px-3 py-1 rounded text-sm font-medium text-black hover:opacity-90"
                        style={{ backgroundColor: ELEGANT_GOLD }}
                      >
                        + Add Item
                      </button>
                    )}
                  </div>
                  <div className="w-full overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b" style={{ borderColor: ELEGANT_GOLD }}>
                          <th className="text-left py-2 px-3 text-sm" style={{ color: ELEGANT_GOLD }}>Item</th>
                          <th className="text-left py-2 px-3 text-sm" style={{ color: ELEGANT_GOLD }}>Category</th>
                          <th className="text-left py-2 px-3 text-sm" style={{ color: ELEGANT_GOLD }}>Quantity</th>
                          <th className="text-left py-2 px-3 text-sm" style={{ color: ELEGANT_GOLD }}>Location</th>
                          <th className="text-left py-2 px-3 text-sm" style={{ color: ELEGANT_GOLD }}>Value</th>
                          <th className="text-right py-2 px-3 text-sm" style={{ color: ELEGANT_GOLD }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inventoryItems.map((item) => (
                          <tr key={item.id} className="border-b border-gray-700 hover:bg-gray-800">
                            <td className="py-2 px-3 text-white font-medium text-sm">{item.item_name}</td>
                            <td className="py-2 px-3 text-gray-300 text-sm">{item.category}</td>
                            <td className="py-2 px-3 text-gray-300 text-sm">{item.quantity} {item.unit}</td>
                            <td className="py-2 px-3 text-gray-300 text-sm">{item.location || 'N/A'}</td>
                            <td className="py-2 px-3 text-gray-300 text-sm">${((item.unit_cost || 0) * item.quantity).toFixed(2)}</td>
                            <td className="py-2 px-3 text-right">
                              <div className="flex gap-2 justify-end">
                                <button onClick={() => onEditItem && onEditItem(item)} className="px-2 py-1 text-xs border rounded hover:bg-gray-700" style={{ borderColor: ELEGANT_GOLD, color: ELEGANT_GOLD }}>
                                  Edit
                                </button>
                                {canDelete && (
                                  <button onClick={() => onDeleteItem && onDeleteItem(item.id)} className="px-2 py-1 text-xs border border-red-500 text-red-500 rounded hover:bg-red-950">
                                    Delete
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              {(recordType === 'task' || recordType === 'tasks') && record && (
                <>
                  {record.address && (
                    <div className="bg-black/40 p-6 rounded-xl border border-[#C9A961]/30 hover:border-[#C9A961]/60 transition-all">
                      <div className="flex items-center gap-3 mb-3">
                        <MapPin className="h-6 w-6 text-[#C9A961]" />
                        <p className="text-xl font-bold text-[#C9A961]">Address</p>
                      </div>
                      <p className="text-white text-lg ml-9">{record.address}</p>
                    </div>
                  )}
                  
                  {record.due_date && (
                    <div className="bg-black/40 p-6 rounded-xl border border-[#C9A961]/30 hover:border-[#C9A961]/60 transition-all">
                      <div className="flex items-center gap-3 mb-3">
                        <Calendar className="h-6 w-6 text-[#C9A961]" />
                        <p className="text-xl font-bold text-[#C9A961]">Deadline</p>
                      </div>
                      <p className="text-white text-lg ml-9">
                        {new Date(record.due_date).toLocaleDateString('en-US', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </p>
                    </div>
                  )}
                  
                  <div className="bg-black/40 p-6 rounded-xl border border-[#C9A961]/30 hover:border-[#C9A961]/60 transition-all lg:col-span-2">
                    <div className="flex items-center gap-3 mb-3">
                      <FileText className="h-6 w-6 text-[#C9A961]" />
                      <p className="text-xl font-bold text-[#C9A961]">Description</p>
                    </div>
                    <p className="text-white text-lg ml-9">{record.description || 'No description provided'}</p>
                  </div>
                  
                  <div className="bg-black/40 p-6 rounded-xl border border-[#C9A961]/30 hover:border-[#C9A961]/60 transition-all lg:col-span-2">
                    <div className="flex items-center gap-3 mb-3">
                      <Users className="h-6 w-6 text-[#C9A961]" />
                      <p className="text-xl font-bold text-[#C9A961]">Assigned Team Members</p>
                    </div>
                    <p className="text-white text-lg ml-9">{getUserNames(record.assigned_to)}</p>
                  </div>
                </>
              )}

              {recordType === 'work-order' && record && (
                <>
                  {record.address && (
                    <div className="bg-black/40 p-6 rounded-xl border border-[#C9A961]/30 hover:border-[#C9A961]/60 transition-all">
                      <div className="flex items-center gap-3 mb-3">
                        <MapPin className="h-6 w-6 text-[#C9A961]" />
                        <p className="text-xl font-bold text-[#C9A961]">Address</p>
                      </div>
                      <p className="text-white text-lg ml-9">{record.address}</p>
                    </div>
                  )}
                  
                  {record.due_date && (
                    <div className="bg-black/40 p-6 rounded-xl border border-[#C9A961]/30 hover:border-[#C9A961]/60 transition-all">
                      <div className="flex items-center gap-3 mb-3">
                        <Calendar className="h-6 w-6 text-[#C9A961]" />
                        <p className="text-xl font-bold text-[#C9A961]">Deadline</p>
                      </div>
                      <p className="text-white text-lg ml-9">
                        {new Date(record.due_date).toLocaleDateString('en-US', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </p>
                    </div>
                  )}
                  
                  <div className="bg-black/40 p-6 rounded-xl border border-[#C9A961]/30 hover:border-[#C9A961]/60 transition-all lg:col-span-2">
                    <div className="flex items-center gap-3 mb-3">
                      <FileText className="h-6 w-6 text-[#C9A961]" />
                      <p className="text-xl font-bold text-[#C9A961]">Description</p>
                    </div>
                    <p className="text-white text-lg ml-9">{record.description || 'No description provided'}</p>
                  </div>
                  
                  <div className="bg-black/40 p-6 rounded-xl border border-[#C9A961]/30 hover:border-[#C9A961]/60 transition-all lg:col-span-2">
                    <div className="flex items-center gap-3 mb-3">
                      <Users className="h-6 w-6 text-[#C9A961]" />
                      <p className="text-xl font-bold text-[#C9A961]">Assigned Team Members</p>
                    </div>
                    <p className="text-white text-lg ml-9">{getUserNames(record.assigned_to)}</p>
                  </div>
                </>
              )}

              {recordType === 'client' && record && (
                <>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Address</p>
                    <p className="text-white mt-1 text-left">{record.address || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Email</p>
                    <p className="text-white mt-1 text-left">{record.email || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Phone</p>
                    <p className="text-white mt-1 text-left">{record.phone || 'N/A'}</p>
                  </div>
                </>
              )}

              {recordType === 'invoice' && record && (
                <>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Invoice Number</p>
                    <p className="text-white mt-1 text-left">{record.invoice_number || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Amount</p>
                    <p className="text-white mt-1 text-left">${record.amount ? record.amount.toFixed(2) : '0.00'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Due Date</p>
                    <p className="text-white mt-1 text-left">{record.due_date ? new Date(record.due_date).toLocaleDateString() : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Status</p>
                    <p className="text-white mt-1 text-left">{record.status ? record.status.toUpperCase() : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Notes</p>
                    <p className="text-white mt-1 text-left">{record.notes || 'N/A'}</p>
                  </div>
                </>
              )}

              {recordType === 'expense' && record && (
                <>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Description</p>
                    <p className="text-white mt-1 text-left">{record.description || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Amount</p>
                    <p className="text-white mt-1 text-left">${record.amount ? record.amount.toFixed(2) : '0.00'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Category</p>
                    <p className="text-white mt-1 text-left">{record.category ? record.category.toUpperCase() : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Expense Date</p>
                    <p className="text-white mt-1 text-left">{record.expense_date ? new Date(record.expense_date).toLocaleDateString() : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Receipt Number</p>
                    <p className="text-white mt-1 text-left">{record.receipt_number || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Notes</p>
                    <p className="text-white mt-1 text-left">{record.notes || 'N/A'}</p>
                  </div>
                </>
              )}

              {recordType === 'contract' && record && (
                <>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Contract Number</p>
                    <p className="text-white mt-1 text-left">{record.contract_number || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Value</p>
                    <p className="text-white mt-1 text-left">${record.value ? record.value.toFixed(2) : '0.00'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Start Date</p>
                    <p className="text-white mt-1 text-left">{record.start_date ? new Date(record.start_date).toLocaleDateString() : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>End Date</p>
                    <p className="text-white mt-1 text-left">{record.end_date ? new Date(record.end_date).toLocaleDateString() : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Status</p>
                    <p className="text-white mt-1 text-left">{record.status ? record.status.toUpperCase() : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Notes</p>
                    <p className="text-white mt-1 text-left">{record.notes || 'N/A'}</p>
                  </div>
                </>
              )}

              {recordType === 'equipment' && record && (
                <>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Equipment Type</p>
                    <p className="text-white mt-1 text-left">{record.equipment_type || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Serial Number</p>
                    <p className="text-white mt-1 text-left">{record.serial_number || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Location</p>
                    <p className="text-white mt-1 text-left">{record.location || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Assigned To</p>
                    <p className="text-white mt-1 text-left">{record.assigned_to || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Purchase Date</p>
                    <p className="text-white mt-1 text-left">{record.purchase_date ? new Date(record.purchase_date).toLocaleDateString() : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Status</p>
                    <p className="text-white mt-1 text-left">{record.status ? record.status.toUpperCase() : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Notes</p>
                    <p className="text-white mt-1 text-left">{record.notes || 'N/A'}</p>
                  </div>
                </>
              )}

              {recordType === 'timesheet' && record && (
                <>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Employee Name</p>
                    <p className="text-white mt-1 text-left">{record.employee_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Date</p>
                    <p className="text-white mt-1 text-left">{record.date ? new Date(record.date).toLocaleDateString() : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Hours Worked</p>
                    <p className="text-white mt-1 text-left">{record.hours_worked ? `${record.hours_worked}h` : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Task Description</p>
                    <p className="text-white mt-1 text-left">{record.task_description || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Notes</p>
                    <p className="text-white mt-1 text-left">{record.notes || 'N/A'}</p>
                  </div>
                </>
              )}

              {recordType === 'inventory' && record && (
                <>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Item Name</p>
                    <p className="text-white mt-1 text-left">{record.item_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Category</p>
                    <p className="text-white mt-1 text-left">{record.category || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Quantity</p>
                    <p className="text-white mt-1 text-left">{record.quantity} {record.unit || ''}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Location</p>
                    <p className="text-white mt-1 text-left">{record.location || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Reorder Level</p>
                    <p className="text-white mt-1 text-left">{record.reorder_level || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Description</p>
                    <p className="text-white mt-1 text-left">{record.description || record.notes || 'N/A'}</p>
                  </div>
                </>
              )}

              {recordType === 'schedule' && record && (
                <>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Employee Name</p>
                    <p className="text-white mt-1 text-left">{record.employee_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Start Time</p>
                    <p className="text-white mt-1 text-left">{record.start_time ? new Date(record.start_time).toLocaleString() : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>End Time</p>
                    <p className="text-white mt-1 text-left">{record.end_time ? new Date(record.end_time).toLocaleString() : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Location</p>
                    <p className="text-white mt-1 text-left">{record.location || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Description</p>
                    <p className="text-white mt-1 text-left">{record.description || 'N/A'}</p>
                  </div>
                </>
              )}

              {recordType === 'safety-report' && record && (
                <>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Severity</p>
                    <p className="text-white mt-1 text-left">{record.severity ? record.severity.toUpperCase() : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Location</p>
                    <p className="text-white mt-1 text-left">{record.location || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Incident Date</p>
                    <p className="text-white mt-1 text-left">{record.incident_date ? new Date(record.incident_date).toLocaleDateString() : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Reported By</p>
                    <p className="text-white mt-1 text-left">{record.reported_by || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Status</p>
                    <p className="text-white mt-1 text-left">{record.status ? record.status.toUpperCase() : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Description</p>
                    <p className="text-white mt-1 text-left">{record.description || 'N/A'}</p>
                  </div>
                </>
              )}

              {recordType === 'certification' && record && (
                <>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Employee Name</p>
                    <p className="text-white mt-1 text-left">{record.employee_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Certification Number</p>
                    <p className="text-white mt-1 text-left">{record.certification_number || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Issue Date</p>
                    <p className="text-white mt-1 text-left">{record.issue_date ? new Date(record.issue_date).toLocaleDateString() : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Expiry Date</p>
                    <p className="text-white mt-1 text-left">{record.expiry_date ? new Date(record.expiry_date).toLocaleDateString() : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Issuing Authority</p>
                    <p className="text-white mt-1 text-left">{record.issuing_authority || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Notes</p>
                    <p className="text-white mt-1 text-left">{record.notes || 'N/A'}</p>
                  </div>
                </>
              )}

              {recordType === 'report' && record && (
                <>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Report Type</p>
                    <p className="text-white mt-1 text-left">{record.report_type || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Period</p>
                    <p className="text-white mt-1 text-left">{record.period || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Generated By</p>
                    <p className="text-white mt-1 text-left">{record.generated_by || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Description</p>
                    <p className="text-white mt-1 text-left">{record.description || 'N/A'}</p>
                  </div>
                </>
              )}

              {recordType === 'compliance' && record && (
                <>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Compliance Type</p>
                    <p className="text-white mt-1 text-left">{record.compliance_type || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Requirement</p>
                    <p className="text-white mt-1 text-left">{record.requirement || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Status</p>
                    <p className="text-white mt-1 text-left">{record.status ? record.status.toUpperCase() : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Due Date</p>
                    <p className="text-white mt-1 text-left">{record.due_date ? new Date(record.due_date).toLocaleDateString() : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Description</p>
                    <p className="text-white mt-1 text-left">{record.description || 'N/A'}</p>
                  </div>
                </>
              )}

              {recordType === 'employee' && record && (
                <>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Email</p>
                    <p className="text-white mt-1 text-left">{record.email || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Phone</p>
                    <p className="text-white mt-1 text-left">{record.phone || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Position</p>
                    <p className="text-white mt-1 text-left">{record.position || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Department</p>
                    <p className="text-white mt-1 text-left">{record.department || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Hire Date</p>
                    <p className="text-white mt-1 text-left">{record.hire_date ? new Date(record.hire_date).toLocaleDateString() : 'N/A'}</p>
                  </div>
                </>
              )}

              {recordType === 'fleet-inspection' && record && (
                <>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Vehicle ID</p>
                    <p className="text-white mt-1 text-left">{record.vehicle_id || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Inspection Date</p>
                    <p className="text-white mt-1 text-left">{record.inspection_date ? new Date(record.inspection_date).toLocaleDateString() : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Inspector</p>
                    <p className="text-white mt-1 text-left">{record.inspector || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Status</p>
                    <p className="text-white mt-1 text-left">{record.status ? record.status.toUpperCase() : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Mileage</p>
                    <p className="text-white mt-1 text-left">{record.mileage || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Notes</p>
                    <p className="text-white mt-1 text-left">{record.notes || 'N/A'}</p>
                  </div>
                </>
              )}

              {recordType === 'inventory-project' && record && (
                <div className="space-y-4 w-full">
                  {/* Project Summary */}
                  <div className="flex gap-6 text-sm bg-gray-800 p-4 rounded">
                    <div className="flex items-center gap-2">
                      <svg className="h-5 w-5" style={{ color: ELEGANT_GOLD }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>
                      <span className="text-gray-400">Total Items:</span>
                      <span className="text-white font-semibold">{record.total_items || 0}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg className="h-5 w-5" style={{ color: ELEGANT_GOLD }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      <span className="text-gray-400">Total Value:</span>
                      <span className="text-white font-semibold">${(record.total_value || 0).toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Inventory Items Table */}
                  <div className="w-full overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b" style={{ borderColor: ELEGANT_GOLD }}>
                          <th className="text-left py-2 px-3" style={{ color: ELEGANT_GOLD }}>Item</th>
                          <th className="text-left py-2 px-3" style={{ color: ELEGANT_GOLD }}>Category</th>
                          <th className="text-left py-2 px-3" style={{ color: ELEGANT_GOLD }}>Quantity</th>
                          <th className="text-left py-2 px-3" style={{ color: ELEGANT_GOLD }}>Location</th>
                          <th className="text-left py-2 px-3" style={{ color: ELEGANT_GOLD }}>Value</th>
                          <th className="text-right py-2 px-3" style={{ color: ELEGANT_GOLD }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inventoryItems && inventoryItems.length > 0 ? inventoryItems.map((item) => (
                          <tr key={item.id} className="border-b border-gray-700 hover:bg-gray-800">
                            <td className="py-2 px-3 text-white font-medium">{item.item_name}</td>
                            <td className="py-2 px-3 text-gray-300">{item.category}</td>
                            <td className="py-2 px-3 text-gray-300">{item.quantity} {item.unit}</td>
                            <td className="py-2 px-3 text-gray-300">{item.location || 'N/A'}</td>
                            <td className="py-2 px-3 text-gray-300">${((item.unit_cost || 0) * item.quantity).toFixed(2)}</td>
                            <td className="py-2 px-3 text-right">
                              <div className="flex gap-2 justify-end">
                                <button onClick={() => onEditItem && onEditItem(item)} className="px-2 py-1 text-xs border rounded hover:bg-gray-700" style={{ borderColor: ELEGANT_GOLD, color: ELEGANT_GOLD }}>
                                  Edit
                                </button>
                                {canDelete && (
                                  <button onClick={() => onDeleteItem && onDeleteItem(item.id)} className="px-2 py-1 text-xs border border-red-500 text-red-500 rounded hover:bg-red-950">
                                    Delete
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        )) : (
                          <tr>
                            <td colSpan="6" className="py-4 text-center text-gray-400">No inventory items for this project</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Add Item Button */}
                  {onAddItem && (
                    <div className="flex justify-start pt-2">
                      <button 
                        onClick={onAddItem}
                        className="px-4 py-2 rounded font-medium text-black hover:opacity-90"
                        style={{ backgroundColor: ELEGANT_GOLD }}
                      >
                        <svg className="h-4 w-4 inline mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                        Add Item to Project
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Generic details for other record types */}
              {!['project', 'task', 'client'].includes(recordType) && record?.description && (
                <div>
                  <p className="font-bold text-left" style={{ color: ELEGANT_GOLD }}>Description</p>
                  <p className="text-white mt-1 text-left">{record.description}</p>
                </div>
              )}
            </div>
          </div>

          {/* AI Form Fill Integration - in Details tab */}
          {(recordType === 'project' || recordType === 'task' || recordType === 'tasks' || recordType === 'work-order') && record && (
            <div className="mb-8">
              <AIFormFill
                formData={record}
                onFillComplete={(data) => {
                  toast.success('AI Form Fill completed! Update the record to save changes.');
                  if (onUpdate) onUpdate();
                }}
                context={`${recordType} management form`}
              />
            </div>
          )}
          </div>
          )}

          {/* Forms Tab */}
          {activeTab === 'forms' && (
            <div className="bg-black/60 backdrop-blur-xl border-2 border-[#C9A961]/50 rounded-2xl p-8 shadow-2xl">
              <FormsList
                recordType={recordType}
                recordId={record?.id}
                canEdit={canDelete}
              />
            </div>
          )}

          {/* Photos Tab */}
          {activeTab === 'photos' && (
            <div className="bg-black backdrop-blur-xl border-2 border-[#C9A961]/50 rounded-2xl p-8 shadow-2xl">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-3xl font-bold text-[#C9A961] flex items-center gap-3">
                  <Image className="h-8 w-8" />
                  Photo Gallery
                </h2>
                <div className="text-sm text-gray-400">
                  {files.filter(f => f.content_type?.includes('image')).length} {files.filter(f => f.content_type?.includes('image')).length === 1 ? 'photo' : 'photos'}
                </div>
              </div>

              {files.filter(f => f.content_type?.includes('image')).length === 0 ? (
                <div className="text-center py-16">
                  <Image className="h-20 w-20 text-[#C9A961]/30 mx-auto mb-6" />
                  <p className="text-gray-400 text-xl mb-2">No photos uploaded yet</p>
                  <p className="text-gray-500 text-sm">Upload images and photos to this gallery</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {files.filter(file => file.content_type?.includes('image')).map((file, index) => (
                    <div key={index} className="relative group">
                      <div 
                        className="relative bg-gray-900 rounded-lg overflow-hidden cursor-pointer border-2 border-transparent hover:border-opacity-100 transition-all"
                        style={{ borderColor: 'transparent' }}
                        onMouseEnter={(e) => e.currentTarget.style.borderColor = ELEGANT_GOLD}
                        onMouseLeave={(e) => e.currentTarget.style.borderColor = 'transparent'}
                        onClick={() => handlePreview(file, files.indexOf(file))}
                      >
                        <div className="relative w-full h-48">
                          <img
                            src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/${file.stored_filename}`}
                            alt={file.filename}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition flex items-center justify-center">
                            <Eye className="h-12 w-12 text-white opacity-0 group-hover:opacity-100 transition" />
                          </div>
                        </div>
                      </div>

                      <div className="mt-2">
                        <p className="text-sm text-white truncate font-medium" title={file.filename}>
                          {file.filename}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {(file.size / 1024).toFixed(2)} KB
                        </p>
                        {file.uploaded_by && (
                          <p className="text-xs text-gray-500">
                            By: {file.uploaded_by}
                          </p>
                        )}
                      </div>

                      <div className="flex gap-1 mt-2">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => handlePreview(file, files.indexOf(file))}
                          className="flex-1 text-xs h-8 hover:bg-gray-800"
                          style={{ color: ELEGANT_GOLD }}
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          View
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => handleDownload(file)}
                          className="flex-1 text-xs h-8 hover:bg-gray-800"
                          style={{ color: ELEGANT_GOLD }}
                        >
                          <Download className="h-3 w-3 mr-1" />
                          Download
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => handleCopyLink(file)}
                          className="text-xs h-8 hover:bg-gray-800"
                          style={{ color: ELEGANT_GOLD }}
                        >
                          <LinkIcon className="h-3 w-3" />
                        </Button>
                        {canDelete && (
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => onDelete(file.id)}
                            className="text-xs h-8 text-red-500 hover:bg-red-950"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div className="bg-black backdrop-blur-xl border-2 border-[#C9A961]/50 rounded-2xl p-8 shadow-2xl">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-3xl font-bold text-[#C9A961] flex items-center gap-3">
                  <FileText className="h-8 w-8" />
                  Documents
                </h2>
                <div className="text-sm text-gray-400">
                  {files.filter(f => !f.content_type?.includes('image')).length} {files.filter(f => !f.content_type?.includes('image')).length === 1 ? 'document' : 'documents'}
                </div>
              </div>

              {files.filter(f => !f.content_type?.includes('image')).length === 0 ? (
                <div className="text-center py-16">
                  <FileText className="h-20 w-20 text-[#C9A961]/30 mx-auto mb-6" />
                  <p className="text-gray-400 text-xl mb-2">No documents uploaded yet</p>
                  <p className="text-gray-500 text-sm">Upload PDFs, Word, Excel, and other documents</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {files.filter(file => !file.content_type?.includes('image')).map((file, index) => (
                    <div key={index} className="relative group">
                      <div 
                        className="relative bg-gray-900 rounded-lg overflow-hidden cursor-pointer border-2 border-transparent hover:border-opacity-100 transition-all"
                        style={{ borderColor: 'transparent' }}
                        onMouseEnter={(e) => e.currentTarget.style.borderColor = ELEGANT_GOLD}
                        onMouseLeave={(e) => e.currentTarget.style.borderColor = 'transparent'}
                        onClick={() => handlePreview(file, files.indexOf(file))}
                      >
                        <div className="w-full h-48 flex items-center justify-center">
                          {getFileIcon(file)}
                        </div>
                      </div>

                      <div className="mt-2">
                        <p className="text-sm text-white truncate font-medium" title={file.filename}>
                          {file.filename}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {(file.size / 1024).toFixed(2)} KB
                        </p>
                        {file.uploaded_by && (
                          <p className="text-xs text-gray-500">
                            By: {file.uploaded_by}
                          </p>
                        )}
                      </div>

                      <div className="flex gap-1 mt-2">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => handlePreview(file, files.indexOf(file))}
                          className="flex-1 text-xs h-8 hover:bg-gray-800"
                          style={{ color: ELEGANT_GOLD }}
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          View
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => handleDownload(file)}
                          className="flex-1 text-xs h-8 hover:bg-gray-800"
                          style={{ color: ELEGANT_GOLD }}
                        >
                          <Download className="h-3 w-3 mr-1" />
                          Download
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => handleCopyLink(file)}
                          className="text-xs h-8 hover:bg-gray-800"
                          style={{ color: ELEGANT_GOLD }}
                        >
                          <LinkIcon className="h-3 w-3" />
                        </Button>
                        {canDelete && (
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => onDelete(file.id)}
                            className="text-xs h-8 text-red-500 hover:bg-red-950"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Enhanced Upload Section at Bottom - Show in Photos and Documents tabs */}
      {(activeTab === 'photos' || activeTab === 'documents') && (
      <div className="border-t-2 border-[#C9A961] p-6 bg-black backdrop-blur-xl shadow-2xl">
        <div className="max-w-7xl mx-auto">
          <Button 
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-full bg-[#C9A961] text-black hover:bg-[#D4C278] hover:scale-105 transition-all py-6 text-xl font-bold shadow-xl"
          >
            <Upload className="h-6 w-6 mr-3" />
            {uploading ? 'Uploading Files...' : '📤 Upload Documents & Photos'}
          </Button>
          <p className="text-center text-gray-400 text-sm mt-3">
            Accepted: Images, PDF, Documents, Spreadsheets
          </p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.md"
            onChange={handleFileUpload}
            className="hidden"
          />
          <p className="text-xs text-gray-500">
            All file types supported: Images, PDFs, Documents, Notes, etc.
          </p>
        </div>
      </div>
      )}

      {/* File Preview Modal */}
      <FilePreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        file={previewFile}
        files={files}
        currentIndex={previewIndex}
        onNavigate={handleNavigate}
        record={record}
        recordType={recordType}
      />
    </div>
  );
};

export default FileGalleryFullScreen;
